import React, { useCallback, useEffect, useState } from "react";
import { getCourseMessages } from "../../api/academic";
import CourseMessagesBlock from "./CourseMessagesBlock";

export default function CourseMessagesDrawer({ open, onClose, runId, courseName }) {
	const [threads, setThreads] = useState([]);
	const [selectedStudentId, setSelectedStudentId] = useState(null);
	const [isTeacherMode, setIsTeacherMode] = useState(false);
	const [loading, setLoading] = useState(false);

	const loadInitial = useCallback(async (clearSelection = false, silent = false) => {
		if (!runId || !open) return;
		if (!silent) setLoading(true);
		if (clearSelection) setSelectedStudentId(null);
		try {
			const data = await getCourseMessages(runId);

			if (Array.isArray(data.threads)) {
				setIsTeacherMode(true);
				setThreads(data.threads || []);
			} else {
				setIsTeacherMode(false);
				setThreads([]);
			}
		} catch {
			setIsTeacherMode(false);
			setThreads([]);
		} finally {
			if (!silent) setLoading(false);
		}
	}, [runId, open]);

	useEffect(() => {
		loadInitial(true, false);
	}, [loadInitial]);

	useEffect(() => {
		if (!open || !runId) return;
		const interval = setInterval(() => loadInitial(false, true), 3000);
		return () => clearInterval(interval);
	}, [open, runId, loadInitial]);

	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e) => {
			if (e.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [open, onClose]);

	if (!runId) return null;

	return (
		<>
			<div
				className={`course-messages-drawer-backdrop ${open ? "open" : ""}`}
				onClick={onClose}
				aria-hidden="true"
			/>
			<aside
				className={`course-messages-drawer ${open ? "open" : ""}`}
				aria-label="Panel de mensajes del curso"
				role="dialog"
				aria-modal="true"
			>
				<div className="course-messages-drawer-inner">
					<header className="course-messages-drawer-header">
						{/* BOTÓN VOLVER */}
						{selectedStudentId && (
							<button
								type="button"
								className="course-messages-drawer-back"
								onClick={() => setSelectedStudentId(null)}
								style={{ border: 'none', background: 'none', marginRight: '10px', cursor: 'pointer' }}
							>
								<i className="ri-arrow-left-line" />
							</button>
						)}

						<h2 className="course-messages-drawer-title">
							{selectedStudentId ? "Conversación" : "Mensajes"}
						</h2>

						<button
							type="button"
							className="course-messages-drawer-close"
							onClick={onClose}
							aria-label="Cerrar panel de mensajes"
						>
							<i className="ri-close-line" aria-hidden />
						</button>
					</header>

					{/* CONTENEDOR PRINCIPAL */}
					<div className="course-messages-drawer-body" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

						{!selectedStudentId ? (
							/* PANTALLA 1: LISTA DE CONTACTOS */
							<div className="course-messages-drawer-contacts" style={{ flex: 1, overflowY: 'auto' }}>
								<div className="course-messages-drawer-categories">
									<div className="course-messages-drawer-category open">
										<div className="course-messages-drawer-category-content">
											{loading ? (
												<p className="course-messages-drawer-loading">Cargando...</p>
											) : isTeacherMode ? (
												threads.length === 0 ? (
													<p className="course-messages-drawer-empty">No hay conversaciones.</p>
												) : (
													threads.map((t) => (
														<button
															key={t.student_id}
															type="button"
															className={`course-messages-drawer-contact-item ${selectedStudentId === t.student_id ? "selected" : ""}`}
															onClick={() => setSelectedStudentId(t.student_id)}
														>
															<span className="course-messages-drawer-contact-avatar">
																<i className="ri-user-line" />
															</span>

															<span className="course-messages-drawer-contact-name text-truncate pe-2" style={{ flex: 1 }}>
																{t.student_name}
															</span>
															{t.has_unread && (
																<span
																	className="bg-danger rounded-circle ms-auto me-2 shadow-sm"
																	style={{ width: '10px', height: '10px', display: 'inline-block' }}
																	title="Nuevo mensaje"
																></span>
															)}

															<i className="ri-arrow-right-s-line" />

														</button>
													))
												)
											) : (
												<button type="button" className="course-messages-drawer-contact-item selected" onClick={() => setSelectedStudentId('teacher')}>
													<span className="course-messages-drawer-contact-avatar"><i className="ri-user-line" /></span>
													<span className="course-messages-drawer-contact-name">Conversación con el docente</span>
													<i className="ri-arrow-right-s-line" />
												</button>
											)}
										</div>
									</div>
								</div>
							</div>
						) : (
							<div className="course-messages-drawer-chat">
								<div className="selected-user-header" >
									{(() => {
										const student = threads.find(t => t.student_id === selectedStudentId);
										return (
											<div className="course-messages-drawer-contact-item selected" style={{ cursor: 'default' }}>
												<span className="course-messages-drawer-contact-avatar">
													<i className="ri-user-line" />
												</span>
												<span className="course-messages-drawer-contact-name" style={{ flex: 1 }}>
													{isTeacherMode ? (student?.student_name || "Alumno") : "Docente del curso"}
												</span>
											</div>
										);
									})()}
								</div>

								<div className="alert alert-warning m-2 p-2 text-center" style={{ fontSize: '0.8rem' }}>
									<i className="ri-error-warning-line me-1"></i>
									Por seguridad, no compartas contraseñas ni datos sensibles por este medio.
								</div>

								{/* BLOQUE DE MENSAJES */}
								<div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
									<CourseMessagesBlock
										runId={runId}
										recipientId={selectedStudentId === 'teacher' ? null : selectedStudentId}
										title=""
										variant="drawer"
										isDrawerOpen={open}
									/>
								</div>
							</div>
						)}
					</div>
				</div>
			</aside>
		</>
	);
}